/* eslint-disable react/display-name */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { faAngleUp, faAngleDown } from '@fortawesome/pro-light-svg-icons';
import {
  AmountDisplay,
  CopyableAsset,
  Box,
  Copyable,
  DropDown,
  FaSvg,
  Hash,
  PaginationSize,
  Table,
  usePagination,
  Pending,
  TableFiltersContainer,
  tableFilterCss,
  themeUtils as tu,
} from '@zen-common/components-base';
import { assetUtils, numberUtils } from '@zen/common-utils';
import {
  ExplorerLink,
  StorageUpdaterStore,
  NamingAssetStore,
  NamingUtils,
} from '@zen/common-app-parts';
import { OPERATIONS, OPERATION_FILTERS, OrderStatus } from '@zen-dex/utils';
import Pair from '../../../components/Pair';
import CancelButton from './CancelButton';

/**
 * The operations table
 *
 * @param {Object} props
 * @param {Array} props.items - the table items
 * @param {string} props.filter - the current filter
 * @param {Function} props.setFilter - a callback function to call when the filter changes
 * @param {boolean} props.loading - the loading status
 */
export default function OperationsTable({ items, filter, setFilter, loading, ...props }) {
  const [expanded, setExpanded] = useState({});
  const storage = StorageUpdaterStore.useStorage();
  const { state: naming } = NamingAssetStore.useStore();
  const pagination = usePagination({
    itemsCount: items.length,
    initialPageSize: storage.get('operations-table-page-size') || undefined, // prevent passing null
  });
  const filterWidthMobile = pagination.showPagination ? '50%' : '100%';

  const isMobile = document.body.clientWidth < tu.breakpoints.xl.replace('px', '');

  StorageUpdaterStore.useUpdater([
    {
      key: 'operations-table-page-size',
      value: pagination.pageSize,
    },
  ]);

  return (
    <Box position="relative">
      {!loading && (
        <TableFiltersContainer>
          <Box css={tableFilterCss} fontSize="sm" mr="sm" width={[filterWidthMobile, 'auto']}>
            <Box as="span" mb={['xxs', '0']}>
              Sort by
            </Box>
            <Box width={['100%', '140px']} ml={['0', 'sm']}>
              <DropDown
                items={filterItems}
                value={filter}
                onChange={({ value }) => {
                  setFilter(value);
                }}
              />
            </Box>
          </Box>
          <PaginationSize
            {...pagination}
            css={tableFilterCss}
            width={[filterWidthMobile, 'auto']}
          />
        </TableFiltersContainer>
      )}
      <Table
        {...props}
        data={items}
        columns={columns(naming, isMobile)}
        loading={loading}
        resizable={false}
        sortable={false}
        minRows={0}
        {...pagination}
        SubComponent={({ original }) =>
          original.type === 'execution' ? (
            <Table
              columns={innerExecutionColumns({
                base: original.data.makeProps.base,
                quote: original.data.makeProps.quote,
                isMobile,
                naming,
              })}
              data={[original]}
              minRows={0}
              sortable={false}
              resizable={false}
            />
          ) : null
        }
        expanded={expanded}
        onPageChange={() => {
          setExpanded({});
        }}
        getTrProps={(state, rowInfo) => {
          return {
            onClick: (e, handleOriginal) => {
              if (
                e.target.classList.contains('rt-td') ||
                e.target.classList.contains('expand-icon')
              ) {
                if (rowInfo.original.type === 'execution') {
                  setExpanded((prevExpanded) => ({
                    [rowInfo.index % pagination.pageSize]: !prevExpanded[
                      rowInfo.index % pagination.pageSize
                    ],
                  }));
                }
              }
              if (handleOriginal) {
                handleOriginal();
              }
            },
          };
        }}
      />
    </Box>
  );
}
OperationsTable.propTypes = {
  filter: PropTypes.string,
  setFilter: PropTypes.func.isRequired,
  items: PropTypes.array,
  loading: PropTypes.bool,
};

const columns = (naming, isMobile) => [
  {
    Header: 'Pair/Asset',
    accessor: '',
    minWidth: isMobile ? 385 : 285, // for the copy button
    Cell: ({ original }) => {
      if (original.type !== 'execution') {
        return <CopyableAsset hash={original.data.asset} naming={naming} />;
      }

      const order = original.data;
      const relevantProps = [OPERATIONS.ORDER_TAKEN, OPERATIONS.TAKE_ORDER].includes(
        order.operation
      )
        ? order.takeProps
        : order.makeProps;
      return <Pair base={relevantProps.base} quote={relevantProps.quote} naming={naming} />;
    },
  },
  {
    Header: 'Tx Hash',
    accessor: 'data.txHash',
    minWidth: 160,
    Cell: ({ value }) => (
      <Copyable value={value}>
        <ExplorerLink path={`/tx/${value}`}>
          <Hash hash={value} />
        </ExplorerLink>
      </Copyable>
    ),
  },
  {
    Header: 'Type',
    accessor: 'type',
    minWidth: 180,
    Cell: (data) => getTypeText(data.original),
  },
  {
    Header: 'Status',
    accessor: '',
    Cell: (data) => getStatusText(data.original),
  },
  {
    Header: '', // amount or cancel button
    accessor: '',
    minWidth: 140,
    Cell: (data) => getAmountOrCancel(data.original),
  },
  {
    expander: true,
    width: 65,
    Expander: ({ isExpanded, original }) => {
      return original.type === 'transaction' ? null : (
        <div>
          <div>
            {isExpanded ? (
              <FaSvg icon={faAngleUp} className="expand-icon" />
            ) : (
              <FaSvg icon={faAngleDown} className="expand-icon" />
            )}
          </div>
        </div>
      );
    },
  },
];

const innerExecutionColumns = ({ base, quote, isMobile, naming } = {}) => [
  {
    Header: 'Price',
    accessor: '',
    Cell: ({ original }) => getOrderPropByOperation({ order: original.data, prop: 'price' }),
  },
  {
    Header: `Quantity ${base ? ' - ' + NamingUtils.displayName(base, naming, isMobile) : ''}`,
    accessor: '',
    Cell: ({ original }) =>
      getOrderPropByOperation({
        order: original.data,
        prop: 'quantity',
      }),
  },
  {
    Header: `Total ${quote ? ' - ' + NamingUtils.displayName(quote, naming, isMobile) : ''}`,
    accessor: '',
    Cell: ({ original }) => getOrderPropByOperation({ order: original.data, prop: 'total' }),
  },
];

/**
 * Get price/quantity/total, differentiate between a make and a take
 */
function getOrderPropByOperation({ order, prop } = {}) {
  return numberUtils.toDisplay(
    [OPERATIONS.ORDER_TAKEN, OPERATIONS.TAKE_ORDER].includes(order.operation)
      ? order.takeProps[prop]
      : order.makeProps[prop]
  );
}

const filterItems = [
  {
    label: 'All',
    value: OPERATION_FILTERS.ALL,
  },
  {
    label: 'Open',
    value: OPERATION_FILTERS.OPEN,
  },
  {
    label: 'Canceled',
    value: OPERATION_FILTERS.CANCELED,
  },
  {
    label: 'Filled',
    value: OPERATION_FILTERS.FILLED,
  },
  {
    label: 'Withdrawal',
    value: OPERATION_FILTERS.TX_SEND,
  },
  {
    label: 'Deposit',
    value: OPERATION_FILTERS.TX_RECEIVE,
  },
  {
    label: 'Pending',
    value: OPERATION_FILTERS.PENDING,
  },
];

function getTypeText(item) {
  if (item.type === 'transaction') {
    return item.data.amount <= 0 ? 'Withdraw' : 'Deposit';
  } else {
    switch (item.data.operation) {
      case OPERATIONS.OPEN_ORDER:
        return `Made a ${isSell(item) ? 'sell' : 'buy'} order`;
      case OPERATIONS.CANCEL_ORDER:
        return `Canceled a ${isSell(item) ? 'sell' : 'buy'} order`;
      case OPERATIONS.TAKE_ORDER:
        return `Took a ${isSell(item) ? 'sell' : 'buy'} order`;
      case OPERATIONS.PARTIAL_RESULT:
        return `Open ${isSell(item) ? 'sell' : 'buy'} order`;
      case OPERATIONS.ORDER_TAKEN:
        return `${isSell(item) ? 'Sell' : 'Buy'} order was taken`;
      default:
        return '';
    }
  }
}

function isSell(item) {
  return [OPERATIONS.TAKE_ORDER, OPERATIONS.ORDER_TAKEN].includes(item.data.operation)
    ? item.data.takeSide === 'sell'
    : item.data.makeSide === 'sell';
}

function getAmountOrCancel(item) {
  return item.type === 'transaction' ? (
    <AmountDisplay
      amount={item.data.amount}
      display={assetUtils.getAmountDisplay(item.data.asset, item.data.amount)}
    />
  ) : item.data.status === OrderStatus.Open && !item.data.pending ? (
    <CancelButton order={item.data} />
  ) : null;
}

function getStatusText(item) {
  if (item.data.pending) {
    return <Pending />;
  } else if (item.type === 'execution') {
    return item.data.status === OrderStatus.Open
      ? 'Open'
      : item.data.status === OrderStatus.Canceled
      ? 'Canceled'
      : 'Filled';
  }

  return 'Confirmed';
}
